<template>
  <StandardWrapper v-bind="$attrs" :blok="blok" width="block">
    <TitleWithRichText
      :blok="{
        ...blok,
        subtitle: displayingLocations ? locationsTitle : blok.subtitle,
      }"
      :title-class="blok.layout === 'cards' ? 'text-center' : ''"
      :subtitle-class="blok.layout === 'cards' ? 'text-center' : ''"
      :rich-text-class="blok.layout === 'cards' ? 'text-center' : ''"
    />

    <ul class="mt-6" :class="listClass">
      <template v-for="(group, index) in vacancyGroups" :key="index">
        <li
          v-if="!displayingLocations || group.vacancyAmount"
          :class="[
            linkClass,
            blok.layout === 'cards' ? 'p-2' : 'mb-3 border-b border-gray-400',
          ]"
        >
          <component
            :is="group.vacancyAmount && blok.urlPrefix ? 'a' : 'div'"
            :class="itemClass"
            :href="`${blok.urlPrefix}/${group.key || group.slug}`"
          >
            <span>{{ group.label || group.name }}</span>
            <span :class="{ 'text-gray-600': blok.layout === 'cards' }"
              >{{ group.vacancyAmount }}
              {{
                blok.layout === 'cards'
                  ? group.vacancyAmount === 1
                    ? 'vacature'
                    : 'vacatures'
                  : ''
              }}</span
            >
          </component>
        </li>
      </template>

      <li
        v-if="blok.layout === 'cards' && blok.displayLinkToAllVacanciesPage"
        class="p-2"
        :class="linkClass"
      >
        <a class="all-vacancies-link" :class="itemClass" href="/vacatures"
          >Alle vacatures</a
        >
      </li>
    </ul>

    <div
      v-if="blok.layout !== 'cards' && blok.displayLinkToAllVacanciesPage"
      class="mt-6 flex justify-center"
      :class="linkClass"
    >
      <a class="all-vacancies-link" href="/vacatures">Alle vacatures</a>
    </div>
  </StandardWrapper>
</template>

<script setup>
import { ref, computed, onServerPrefetch, onBeforeMount, watch } from 'vue'
import StandardWrapper from '../components/StandardWrapper.vue'
import TitleWithRichText from '../components/TitleWithRichText.vue'
import { useColorPalette } from '../composables/colorPalette'
import { useMainStore } from '../store/index.js'
import {
  fetchCompanyList,
  fetchVacancyCategoryOverview,
  fetchVacancyLocationOverview,
} from '../api/tooContentDelivery'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const store = useMainStore()
const { textColorClass, colorName } = useColorPalette()

const vacancyGroups = ref([])

const displayingLocations = computed(() => {
  return (
    props.blok.category === 'regions' &&
    (props.context.searchParams?.tagCategory === 'REGION' ||
      props.context.searchParams?.tagCategory === 'LOCATION')
  )
})
const locationsTitle = computed(() => {
  let regionLabel = props.context.searchParams?.region
  regionLabel = regionLabel.replace(/(?:^|-)([a-z])/g, (match) =>
    match.toUpperCase(),
  )

  return `Vacatures in ${regionLabel}`
})
const listClass = computed(() => {
  if (props.blok.layout === 'cards') {
    return `grid md:grid-cols-2 lg:grid-cols-${props.blok.columns} text-gray-800 -ml-2`
  } else {
    return `md:columns-2 lg:columns-${props.blok.columns} md:gap-8 lg:gap-12 ${textColorClass(props.blok.textVariant, props.blok.bgVariant)}`
  }
  // We add this so these Tailwind classes are included in the bundle
  // lg:grid-cols-1 lg:grid-cols-2 lg:grid-cols-3 lg:grid-cols-4 lg:grid-cols-5 lg:grid-cols-6
  // lg:columns-1 lg:columns-2 lg:columns-3 lg:columns-4 lg:columns-5 lg:columns-6
})
const linkClass = computed(() => {
  if (props.blok.linkTextColor) {
    return `richtext-link ${colorName(props.blok.linkTextColor)}`
  } else {
    return 'text-current'
  }
})
const itemClass = computed(() => {
  if (props.blok.layout === 'cards') {
    return `flex flex-col justify-center items-center bg-white p-4 h-full rounded border-gray-300 border`
  } else {
    return 'flex justify-between'
  }
})

async function fetchCategories() {
  if (props.blok.category === 'companies') {
    const { data } = await fetchCompanyList({
      searchParams: {},
      page: {
        page: 1,
        size: 999,
      },
      domainSlug: props.context.domainSlug,
      baseTooUrl: props.context.baseTooUrl,
    })
    vacancyGroups.value = data.map((company) => {
      return {
        key: company.slug,
        label: company.name,
        vacancyAmount: company.vacancyAmount,
      }
    })
  } else if (
    props.blok.category === 'regions' &&
    (props.context.searchParams?.tagCategory === 'REGION' ||
      props.context.searchParams?.tagCategory === 'LOCATION')
  ) {
    vacancyGroups.value = await fetchVacancyLocationOverview({
      region: props.context.searchParams.region,
      domainSlug: props.context.domainSlug,
      baseTooUrl: props.context.baseTooUrl,
    })
  } else {
    vacancyGroups.value = await fetchVacancyCategoryOverview({
      category: props.blok.category,
      domainSlug: props.context.domainSlug,
      baseTooUrl: props.context.baseTooUrl,
    })
  }

  if (props.blok.orderBy === 'amount') {
    // Sort in descending order by vacancy amount
    vacancyGroups.value.sort((a, b) => b.vacancyAmount - a.vacancyAmount)
  } else {
    // Sort in ascending order alphabetically
    vacancyGroups.value.sort((a, b) =>
      a.name?.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    )
  }

  const maxItems = parseInt(props.blok.maxItems) || 0
  if (maxItems > 0) {
    vacancyGroups.value = vacancyGroups.value.slice(0, maxItems)
  }
}

onServerPrefetch(async () => {
  await fetchCategories()
  store.prefetchedData[props.blok._uid] = vacancyGroups.value
})

onBeforeMount(() => {
  if (store.prefetchedData[props.blok._uid]) {
    vacancyGroups.value = store.prefetchedData[props.blok._uid]
  } else {
    fetchCategories()
  }
})
</script>
